import React from 'react';
import { SandpackProvider, SandpackPreview } from '@codesandbox/sandpack-react';
import type { SandboxErrorHandler } from './SandboxErrorBoundary';
import { SandboxErrorBoundary } from './SandboxErrorBoundary';
import { getSandboxErrorHandler } from './sandboxErrorConfig';
import { SandboxErrorReporter } from './SandboxErrorReporter';
import { SandboxTimeoutGuard } from './SandboxTimeoutGuard';

export interface SandboxRuntimeProps {
  files: Record<string, string | { code: string; readOnly?: boolean }>;
  externalResources?: string[];
  style?: React.CSSProperties;
  /** Called on both infrastructure failures and user JSX runtime errors.
   *  Falls back to the global handler set via setSandboxErrorHandler(). */
  onError?: SandboxErrorHandler;
}

/**
 * Self-contained sandboxed preview — wraps SandpackProvider + SandpackPreview.
 * Use this for read-only rendering of user-authored components (e.g. widget cards).
 */
export function SandboxRuntime({ files, externalResources, style, onError }: SandboxRuntimeProps): React.ReactElement {
  const handler = onError ?? getSandboxErrorHandler() ?? undefined;

  return (
    <SandboxErrorBoundary onError={handler}>
      <SandpackProvider
        template="react"
        files={files}
        options={{
          recompileMode: 'delayed',
          recompileDelay: 500,
          externalResources,
        }}
        style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        <SandpackPreview
          style={style ?? { height: '100%', flexGrow: 1 }}
          showOpenInCodeSandbox={false}
          showRefreshButton={false}
        />
        <SandboxTimeoutGuard onError={handler} />
        <SandboxErrorReporter onError={handler} />
      </SandpackProvider>
    </SandboxErrorBoundary>
  );
}
